import {
  readEntitiesByIDs,
  watchEntitiesByIDs,
  readAllEntities,
  watchAllEntities,
} from '@adornis/bh-util/server/entity-reads.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { context } from '@adornis/baseql/server/context.js';
import { runInServerContext } from '@adornis/baseql/server/server.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { checkAuthorizedMiddleware } from '@adornis/bh-matching-user/checkAuthorizedMiddleware.js';
import { BuddyUser, BuddyUserMentee, BuddyUserMentor } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { MatchingPermission } from '@adornis/bh-matching-user/permissions.js';
import { mongo } from '@adornis/bh-util/server/mongo.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { merge, share, Subject, switchMap, map, of } from 'rxjs';
import { Match, MatchComment } from '../entities/match.js';
import './auto-matching.js';

const _onMatchingCreated = new Subject<string>();
export const onMatchCreated = _onMatchingCreated.pipe(share());

// ==================== create ===================
const createMatches = registerMutation({
  type: () => [String],
  operationName: 'createMatches',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  params: [{ type: () => [Match], name: 'matches' }],
  resolve: (matches: Match[]) => {
    return () =>
      runInServerContext(async () => {
        for (const match of matches) {
          match.comments = [new MatchComment({ text: 'Match wurde erstellt', createdAt: new Date() })];
          await match.create();
          _onMatchingCreated.next(match._id);
        }
        return matches.map(m => m._id);
      });
  },
  options: {},
});

// ==================== reminder mails ===================

const _filterAndOptionallyPaginateMatches = (
  filter: AdornisFilter,
  skip?: number,
  limit?: number,
  timeframeID?: string,
) => {
  return mongo()
    .collection<Match>(Match._collectionName)
    .aggregate(
      [
        ...(timeframeID ? [{ $match: { timeframeID } }] : []),
        { $lookup: { from: BuddyUser._collectionName, as: 'mentor', localField: 'mentorID', foreignField: '_id' } },
        { $set: { mentor: { $arrayElemAt: ['$mentor', 0] } } },
        { $lookup: { from: BuddyUser._collectionName, as: 'mentee', localField: 'menteeID', foreignField: '_id' } },
        { $set: { mentee: { $arrayElemAt: ['$mentee', 0] } } },
        {
          $lookup: {
            from: BuddyUser._collectionName,
            as: 'mentee.preferedMentors',
            localField: 'mentee.preferedMentorsIDs',
            foreignField: '_id',
          },
        },
        { $match: filter.getMongoFilter() },

        { $sort: filter.getMongoOptions().sort },
        ...(skip ? [{ $skip: skip }] : []),
        ...(limit ? [{ $limit: limit }] : []),
      ],
      { collation: { locale: 'en', strength: 1 } },
    )
    .toArray();
};
const getMatchesFiltered = registerQuery({
  operationName: 'getMatchesFiltered',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve:
    (timeframeID: string, filter?: AdornisFilter) =>
    async <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      await _filterAndOptionallyPaginateMatches(filter ?? new AdornisFilter({}), undefined, undefined, timeframeID),
  type: () => [Match],
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const getMachesCount = registerQuery({
  operationName: 'getMachesCount',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve: (timeframeID: string, filter?: AdornisFilter) => async () =>
    await _filterAndOptionallyPaginateMatches(filter ?? new AdornisFilter({}), undefined, undefined, timeframeID).then(
      matches => matches.length,
    ),
  type: () => Int,
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const getMatchUserCount = async (timeframeID: string, filter: AdornisFilter, userType: 'mentee' | 'mentor') => {
  const mongoFilter = filter.getMongoFilter();
  const aggregation = [
    ...(timeframeID ? [{ $match: { timeframeID } }] : []),
    { $lookup: { from: BuddyUser._collectionName, as: 'mentor', localField: 'mentorID', foreignField: '_id' } },
    { $set: { mentor: { $arrayElemAt: ['$mentor', 0] } } },
    { $lookup: { from: BuddyUser._collectionName, as: 'mentee', localField: 'menteeID', foreignField: '_id' } },
    { $set: { mentee: { $arrayElemAt: ['$mentee', 0] } } },
    {
      $lookup: {
        from: BuddyUser._collectionName,
        as: 'mentee.preferedMentors',
        localField: 'mentee.preferedMentorsIDs',
        foreignField: '_id',
      },
    },
    ...(mongoFilter ? [{ $match: mongoFilter }] : []),
    { $match: { [userType + '._id']: { $exists: true, $ne: null } } },
    { $group: { _id: '$' + userType + 'ID' } },
    { $group: { _id: null, count: { $sum: 1 } } },
  ];

  const matchesCollection = mongo().collection(Match._collectionName);
  const result = await matchesCollection.aggregate(aggregation, { collation: { locale: 'en', strength: 1 } }).toArray();

  return result[0]?.count ?? 0;
};

const _getMenteesWithMatchesCount = registerQuery({
  operationName: 'getMenteesWithMatchesCount',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve: (timeframeID: string, filter?: AdornisFilter) => () =>
    getMatchUserCount(timeframeID, filter as any, 'mentee'),
  type: () => Int,
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const _getMentorsWithMatchesCount = registerQuery({
  operationName: 'getMentorsWithMatchesCount',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve: (timeframeID: string, filter?: AdornisFilter) => async () =>
    getMatchUserCount(timeframeID, filter as any, 'mentor'),
  type: () => Int,
  params: [
    { name: 'timeframeID', type: () => ID },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const subscribeMatchesPaginatedAndFiltered = registerSubscription({
  operationName: 'subscribeMatchesPaginatedAndFiltered',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve:
    (skip: number, limit: number, filter: AdornisFilter, timeframeID?: string) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) => {
      return merge(watchEntireCollection(Match._collectionName), watchEntireCollection(BuddyUser._collectionName)).pipe(
        switchMap(async () => _filterAndOptionallyPaginateMatches(filter, skip, limit, timeframeID)),
      );
    },
  type: () => [Match],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'timeframeID', type: () => ID },
  ],
});

const getMailAddressesOfMatches = registerQuery({
  operationName: 'getMailAddressesOfMatches',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve: (timeframeID: string, filter: AdornisFilter, userType?: 'mentee' | 'mentor') => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);

    if (!userType) return matches.flatMap(match => [match.mentor?.email, match.mentee?.email]).filter(Boolean);
    if (userType === 'mentee') return matches.map(match => match.mentee?.email).filter(Boolean);
    if (userType === 'mentor') return matches.map(match => match.mentor?.email).filter(Boolean);
  },
  type: () => [String],
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'userClass', type: () => String },
  ],
});

const getUserIDsOfMatches = registerQuery({
  operationName: 'getUserIDsOfMatches',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  resolve: (timeframeID: string, filter: AdornisFilter, userType?: 'mentee' | 'mentor') => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);

    if (!userType) return matches.flatMap(match => [match.mentor?._id, match.mentee?._id]).filter(Boolean) as string[];
    if (userType === 'mentee') return matches.map(match => match.mentee?._id).filter(Boolean) as string[];
    if (userType === 'mentor') return matches.map(match => match.mentor?._id).filter(Boolean) as string[];
  },
  type: () => [String],
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
    { name: 'userClass', type: () => String },
  ],
});

const getIDsOfMatches = registerQuery({
  operationName: 'getIDsOfMatches',
  middlewares: [
    checkAuthorizedMiddleware(({ user }) => user.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_MATCHES)),
  ],
  type: () => [String],
  resolve: (timeframeID: string, filter: AdornisFilter) => async () => {
    const matches = await _filterAndOptionallyPaginateMatches(filter, undefined, undefined, timeframeID);
    return matches.map(match => match._id);
  },
  params: [
    { name: 'timeframeID', type: () => String },
    { name: 'filter', type: () => AdornisFilter },
  ],
});

const _getAllEmailAddressFromMatch = (curMatch: Match, userType?: typeof BuddyUserMentee | typeof BuddyUserMentor) => {
  const allMails: string[] = [];

  if (!userType || userType._class === BuddyUserMentee._class)
    if (curMatch.mentee?.email) allMails.push(curMatch.mentee?.email);

  if (!userType || userType._class === BuddyUserMentor._class)
    if (curMatch.mentor?.email) allMails.push(curMatch.mentor?.email);

  return allMails;
};

const _getAllEmailAddressFromMatches = (
  matches: Match[],
  userType?: typeof BuddyUserMentee | typeof BuddyUserMentor,
) => {
  return matches.flatMap(match => _getAllEmailAddressFromMatch(match, userType));
};

// ==================== namespace ===================
export namespace MatchAPI {
  /**
   * @returns id array of created matches
   */

  export const create = createMatches;
  export const subscribePaginatedAndFiltered = subscribeMatchesPaginatedAndFiltered;
  export const getMailAddresses = getMailAddressesOfMatches;
  export const getUserIDs = getUserIDsOfMatches;
  export const getMatchesIDs = getIDsOfMatches;
  export const getAllEmailAddressFromMatch = _getAllEmailAddressFromMatch;
  export const getAllEmailAddressFromMatches = _getAllEmailAddressFromMatches;
  export const getFiltered = getMatchesFiltered;
  export const getMatchesCount = getMachesCount;
  export const getMenteesWithMatchesCount = _getMenteesWithMatchesCount;
  export const getMentorsWithMatchesCount = _getMentorsWithMatchesCount;
}

export const addCommentOnMatch = registerMutation({
  operationName: 'addCommentOnMatch',
  resolve: (matchID: string, comment: string) => async () => {
    const currUser = await getMyself<BuddyUser>()({ roles: selectionSet(() => AdornisRoleToContextsWrapper, 2) });
    if (!context.serverContext && !currUser?.hasPermission(MatchingPermission.CREATE_EDIT_DELETE_USERS))
      throw new Error('No permission to edit notes on users');

    const updateResult = await mongo()
      .collection(Match._collectionName)
      .updateOne(
        { _id: matchID as any },
        { $push: { comments: new MatchComment({ text: comment, createdAt: new Date() }).toJSON() } as any },
      );
    if (updateResult.modifiedCount === 1) return true;
    throw new Error(`Couldnt add comment on match (matchID: ${matchID}; ${JSON.stringify(updateResult)})`);
  },
  type: () => Boolean,
  params: [
    { name: 'matchID', type: () => String },
    { name: 'comment', type: () => String },
  ],
  options: { allowHTTP: true },
});

export const getMatchesByIDs = registerQuery({
  operationName: 'getMatchesByIDs',
  type: () => [Match],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      readEntitiesByIDs(Match, ids, gqlFields),
});

export const subscribeMatchesByIDs = registerSubscription({
  operationName: 'subscribeMatchesByIDs',
  type: () => [Match],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(Match, ids, gqlFields),
});

export const getAllMatches = registerQuery({
  operationName: 'getAllMatches',
  type: () => [Match],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      readAllEntities(Match, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllMatches = registerSubscription({
  operationName: 'subscribeAllMatches',
  type: () => [Match],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
      watchAllEntities(Match, gqlFields, { skip, limit, excludeIDs }),
});

export const getMatchByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getMatchesByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeMatchByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<Match>>(gqlFields: SelSet) =>
    id ? subscribeMatchesByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
